import '@/globals/css/base.css' // must be first — establishes @layer order before lab-components IIFE runs
import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'
import { useAuthStore } from '@/stores/authStore'
import { useBrandStore } from '@/stores/brandStore'
import { usePwaInstallStore } from '@/stores/pwaInstallStore'
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { i18n, loadRemoteI18n, SUPPORTED_LOCALES, t, type TAppLocale } from '@/locales'
import { ENV } from './globals/typescript/utils/host'
import { initSentry } from './globals/typescript/utils/sentry'
import { VersionCheckPlugin } from '@team-milva/lab-vue-plugins'
import { handleCriticalError } from '@/globals/typescript/utils/handleCriticalError'
import { registerServiceWorker } from '@/globals/typescript/utils/registerServiceWorker'

async function bootstrap() {
  const pinia = createPinia() // create the instance

  // Before the first await: the browser's install event can fire as soon as the page is
  // installable, which is earlier than the translations below finish loading.
  usePwaInstallStore(pinia).init()

  // Ensure translations are up-to-date before app mounts
  await loadRemoteI18n()
  const app = createApp(App)

  // Fire-and-forget: the live PublicLabAccounts fetch must never block first paint (a
  // slow or offline fetch leaves the bundled brand guess in place — see brandStore.ts).
  void useBrandStore(pinia).resolveBrand()

  // This app supports all 5 of @team-milva/lab-models' TLabCustomerLocale — see
  // SUPPORTED_LOCALES in @/locales for why a local, narrower type still mirrors
  // it rather than importing TLabCustomerLocale directly.
  const savedLang = localStorage.getItem('lang') as TAppLocale | null
  // Falls back to the build's bundled brand default (plain 'en' when no brand is
  // configured) rather than a hardcoded 'en' — see "Per-Lab Branding for the Customer
  // App" plan, Phase 4. Only the bundled guess is used here, synchronously available
  // before mount; the live document's defaultLocale (if it differs) does not re-trigger
  // a locale change mid-session once the UI is already showing.
  let resolvedLang: TAppLocale = useBrandStore(pinia).defaultLocale

  if (savedLang && SUPPORTED_LOCALES.includes(savedLang)) {
    resolvedLang = savedLang
  } else {
    const browserLanguages = navigator.languages?.length
      ? navigator.languages
      : [navigator.language]
    for (const lang of browserLanguages) {
      const langCode = (lang.split('-')[0] ?? '').toLowerCase() as TAppLocale
      if (SUPPORTED_LOCALES.includes(langCode)) {
        resolvedLang = langCode
        break
      }
    }
  }

  i18n.global.locale.value = resolvedLang

  app.use(i18n)
  app.use(pinia)
  app.use(router)
  app.use(VersionCheckPlugin, {
    currentVersion: import.meta.env.VITE_APP_VERSION,
    overlayText: () => t('message.updating-to-latest-version'),
    router,
    disabled: false, // import.meta.env.MODE === 'development',
    onError: ({ error, latestVersion, cachedVersion, reloadTargetVersion }) =>
      handleCriticalError({
        error,
        code: 'version-update-error',
        debugMessages: [
          `latestVersion: ${latestVersion}`,
          `cachedVersion: ${cachedVersion}`,
          `reloadTargetVersion: ${reloadTargetVersion}`,
        ],
        title: 'Version update fail',
      }),
  })
  app.provide('translateFormError', getErrorMessage)

  // init auth before mounting + before first route decisions
  const authStore = useAuthStore(pinia) // pass pinia when outside setup()
  await authStore.init()

  if (!ENV.isLocalhost) {
    initSentry({ app, router })
  }

  app.mount('#app')

  registerServiceWorker()
}

bootstrap()

